import React, { useEffect, useState } from "react";
import { format, parseISO, isAfter } from "date-fns";
import { useNavigate } from "react-router-dom";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";
import { cn } from "@/lib/utils";
import ApiService from "@/services/api";
import { EndpointKeys } from "@/config/api";
import { SubmissionResponse } from "@/components/submission/types";
import MenuBar from "@/components/MenuBar";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Menu } from "lucide-react";
import { Loader2 } from "lucide-react";

interface Props {}

export const NannySubmissionsList: React.FC<Props> = () => {
  const [submissions, setSubmissions] = useState<SubmissionResponse[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [reopeningId, setReopeningId] = useState<number | null>(null);
  const [latestSubmissionDate, setLatestSubmissionDate] = useState<Date | null>(null);
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    fetchSubmissions();
  }, []);

  const fetchSubmissions = async () => {
    try {
      setIsLoading(true);
      const response = await ApiService.request<SubmissionResponse[]>(
        EndpointKeys.GET_EMPLOYEE_SUBMISSIONS
      );
      setSubmissions(response);
      
      // Find the latest submission date
      if (response.length > 0) {
        const dates = response.map(sub => parseISO(sub.submission_month));
        const latestDate = dates.reduce((latest, current) => 
          isAfter(current, latest) ? current : latest
        );
        setLatestSubmissionDate(latestDate);
      }
    } catch (err) {
      toast({
        title: "Error",
        description:
          err instanceof Error ? err.message : "Failed to fetch submissions",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleViewSubmission = (submissionId: number) => {
    navigate(`/nanny/submission/${submissionId}`);
  };

  const handleReopenSubmission = async (submissionId: number) => {
    try {
      setReopeningId(submissionId);
      
      await ApiService.request(EndpointKeys.REOPEN_SUBMISSION, {
        method: "POST",
        urlParams: { id: submissionId },
      });
      
      toast({
        title: "Success",
        description: "Submission reopened successfully. All rejected items have been restored to pending status.",
      });
      
      // Refresh submissions list
      await fetchSubmissions();
      
      // Navigate to the submission edit page
      navigate(`/nanny/submission/${submissionId}`);
    } catch (err) {
      toast({
        title: "Error",
        description: 
          err instanceof Error ? err.message : "Failed to reopen submission",
        variant: "destructive",
      });
    } finally {
      setReopeningId(null);
    }
  };

  // Function to check if a submission is the latest one
  const isLatestSubmission = (submission: SubmissionResponse): boolean => {
    if (!latestSubmissionDate) return false;
    const submissionDate = parseISO(submission.submission_month);
    return submission.status === "REJECTED" && 
           (submissionDate.getTime() === latestSubmissionDate.getTime());
  };

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-background">
      {/* Mobile Menu Toggle */}
      <div className="fixed top-0 left-0 right-0 z-30 flex items-center bg-gray-900 p-4 md:hidden">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="text-white"
        >
          <Menu className="h-6 w-6" />
        </Button>
      </div>

      {/* Sidebar */}
      <div
        className={`${
          isMobileMenuOpen ? "block" : "hidden"
        } fixed h-full w-64 bg-gray-900 md:relative md:block md:w-64`}
      >
        <MenuBar />
      </div>

      {/* Main Content */}
      <div className="flex-1 w-full md:w-auto mt-16 md:mt-0">
        <div className="p-4 md:p-6">
          <Card>
            <CardHeader>
              <CardTitle>My Monthly Submissions</CardTitle>
              <CardDescription>
                Every pay period, we automatically create a submission form for you to fill out.
                You can record your hours worked, time off taken, and any expenses through these submissions.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ScrollArea className="w-full">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Month</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="hidden md:table-cell">
                          Submitted
                        </TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {submissions.map((submission) => (
                        <TableRow key={submission.id}>
                          <TableCell>
                            {format(
                              parseISO(submission.submission_month),
                              "MMM yyyy"
                            )}
                          </TableCell>
                          <TableCell>
                            <span
                              className={cn(
                                "rounded-full px-2 py-1 text-xs font-medium",
                                {
                                  "bg-yellow-100 text-yellow-800":
                                    submission.status === "SUBMITTED",
                                  "bg-green-100 text-green-800":
                                    submission.status === "APPROVED",
                                  "bg-red-100 text-red-800":
                                    submission.status === "REJECTED",
                                  "bg-gray-100 text-gray-800":
                                    submission.status === "DRAFT",
                                }
                              )}
                            >
                              {submission.status}
                            </span>
                          </TableCell>
                          <TableCell className="hidden md:table-cell">
                            {submission.submitted_at
                              ? format(
                                  new Date(submission.submitted_at),
                                  "PP pp"
                                )
                              : "Not submitted"}
                          </TableCell>
                          <TableCell>
                            {submission.status === "REJECTED" ? (
                              isLatestSubmission(submission) ? (
                                <Button
                                  onClick={() => handleReopenSubmission(submission.id)}
                                  variant="outline"
                                  size="sm"
                                  disabled={reopeningId === submission.id}
                                  className="bg-secondary hover:bg-secondary/90 text-secondary-foreground border-0"
                                >
                                  {reopeningId === submission.id ? (
                                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                  ) : null}
                                  Reopen
                                </Button>
                              ) : (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  disabled={true}
                                  className="opacity-50 cursor-not-allowed"
                                >
                                  Cannot Reopen
                                </Button>
                              )
                            ) : (
                              <Button
                                onClick={() => handleViewSubmission(submission.id)}
                                variant="outline"
                                size="sm"
                                data-testid="edit-submission-button"
                              >
                                {submission.status === "DRAFT" ? "Edit" : "View"}
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default NannySubmissionsList; 